/**
 * Data-sufficiency math (FE port).
 *
 * MIRROR of `core/src/app/segments/data_sufficiency.py` — keep the
 * two implementations in sync. The BE is the source of truth for
 * actual model creation (it returns 400 if the pipeline won't
 * train); this FE port is purely for live preview ("would my
 * current settings work?") to avoid round-trip latency.
 *
 * Math is KIND-AWARE because trainers behave differently:
 *   - 'custom'             -> classical train/val/test split, each
 *                              must hold one sliding window.
 *   - 'foundation'         -> single sliding window over the full
 *                              segment, no splits. Only constraint:
 *                              lookback + horizon <= n_rows.
 *   - 'byom'               -> no training, skip.
 *
 * If you change one, change the other.
 */

const DEFAULT_TEST_SPLIT = 0.2;
const DEFAULT_VALIDATION_SPLIT = 0.2;

/**
 * Cheap analytical estimator for segment row count.
 *
 * Matches `_estimate_segment_rows` in
 * core/src/app/models/foundation/external/contract.py:
 *
 *   row_count = floor(tail_seconds / interval) + 1
 *
 * Returns null when the segment doesn't carry enough metadata.
 */
export function estimateSegmentRows(segment) {
  if (!segment || typeof segment !== 'object') return null;
  const interval = Number(segment.interval);
  const tail = segment.tail_seconds;
  if (!Number.isFinite(interval) || interval <= 0) return null;
  if (tail === null || tail === undefined) return null;
  const tailSeconds = Number(tail);
  if (!Number.isFinite(tailSeconds) || tailSeconds < 0) return null;
  return Math.floor(tailSeconds / Math.max(1, interval)) + 1;
}

/**
 * Compute split row counts using the trainer's exact convention:
 *   1. test split is taken first (off the tail)
 *   2. validation split is taken from the remaining train+val portion
 *      (so val_split is a fraction of (1 - test_split))
 *   3. training set is what's left
 *
 * Returns { trainRows, valRows, testRows, totalRows }.
 */
export function computeSplitSizes(totalRows, testSplit, valSplit) {
  const ts = clamp(Number(testSplit), 0, 0.5);
  const vs = clamp(Number(valSplit), 0, 0.5);
  const testRows = Math.floor(totalRows * ts);
  const trainValRows = totalRows - testRows;
  const valRows = Math.floor(trainValRows * vs);
  const trainRows = trainValRows - valRows;
  return { trainRows, valRows, testRows, totalRows };
}

/**
 * Horizon-aware ADAPTIVE split sizing — mirror of the trainer's
 * `resolve_split_rows` (core/src/app/models/model.py). The trainer no
 * longer fails when the nominal fractions leave a split without one
 * full sliding window (minWindow = sequence_length + forecast_horizon
 * consecutive rows); it resizes:
 *   - all splits already hold a window -> keep the fractions;
 *   - totalRows >= 3 * minWindow -> grow val/test to one window each
 *     (shrinking overshoot back, test first, so train keeps a window);
 *   - 2 * minWindow <= totalRows < 3 * minWindow -> train + val get one
 *     window each, test gets the leftovers (possibly zero sequences —
 *     tolerated, test metrics are just skipped);
 *   - totalRows < 2 * minWindow -> not trainable (fractions kept).
 *
 * Returns { trainRows, valRows, testRows, totalRows }.
 */
export function computeAdaptiveSplitSizes(totalRows, testSplit, valSplit, minWindow) {
  const nominal = computeSplitSizes(totalRows, testSplit, valSplit);
  const w = Math.max(1, parseInt(minWindow, 10) || 1);
  let { trainRows, valRows, testRows } = nominal;

  if (Math.min(trainRows, valRows, testRows) >= w) return nominal;

  if (totalRows >= 3 * w) {
    testRows = Math.max(testRows, w);
    valRows = Math.max(valRows, w);
    let deficit = w - (totalRows - testRows - valRows);
    if (deficit > 0) {
      const give = Math.min(deficit, testRows - w);
      testRows -= give;
      deficit -= give;
    }
    if (deficit > 0) valRows -= Math.min(deficit, valRows - w);
  } else if (totalRows >= 2 * w) {
    valRows = w;
    testRows = Math.max(0, Math.min(testRows, totalRows - 2 * w));
  } else {
    return nominal;
  }
  trainRows = totalRows - testRows - valRows;
  return { trainRows, valRows, testRows, totalRows };
}

/**
 * Validate the (segment, sequence_length, forecast_horizon, data_split,
 * kind) combination. Returns:
 *
 *   {
 *     mode:    "split" | "single",             // matches the trainer
 *     status:  "ok" | "warn" | "error" | "unknown",
 *     reason:  string,                         // user-facing message
 *     totalRows: number,
 *     minWindow: number,
 *     // Only populated in "split" mode:
 *     splits:    { trainRows, valRows, testRows, totalRows },
 *     sequences: { train, val, test },
 *     // Only populated in "single" mode (foundation kinds):
 *     windowCount: number,                     // sliding windows that fit
 *     suggested: { horizon, sequenceLength, totalRows }, // when status !== "ok"
 *   }
 *
 * - "unknown" when row count can't be estimated (segment metadata is
 *   incomplete) — UI should show "we'll know once you pick a segment"
 *   or hide the preview.
 *
 * Mode selection:
 *   - kind === 'custom' (or unspecified): "split" mode. Each of
 *     train/val/test must hold >=1 window for status='ok'.
 *   - kind === 'foundation': "single" mode. Only need
 *     totalRows >= minWindow; no train/val/test split.
 */
export function validateDataSufficiency({
  segment,
  sequenceLength,
  forecastHorizon,
  dataSplit,
  kind = 'custom',
}) {
  const seqLen = parseInt(sequenceLength, 10);
  const horizon = parseInt(forecastHorizon, 10);
  const kindNorm = String(kind || 'custom').toLowerCase();
  const isFoundation = kindNorm === 'foundation';
  const mode = isFoundation ? 'single' : 'split';

  if (!Number.isFinite(seqLen) || seqLen < 1 ||
      !Number.isFinite(horizon) || horizon < 1) {
    return { mode, status: 'unknown', reason: 'sequence_length and forecast_horizon must be positive integers.' };
  }

  const totalRows = estimateSegmentRows(segment);
  if (totalRows === null || totalRows < 1) {
    return {
      mode,
      status: 'unknown',
      reason: 'Segment does not carry a row-count estimate (missing interval / tail_seconds).',
      minWindow: seqLen + horizon,
    };
  }

  const minWindow = seqLen + horizon;

  // ---- Foundation kinds: single-window check, no splits ----------------
  if (isFoundation) {
    const windowCount = Math.max(0, totalRows - minWindow + 1);
    if (windowCount >= 1) {
      // Single-window mode is binary (fits / doesn't). Mark "warn"
      // when only a handful of windows fit (low signal for adapter
      // training) so users can opt to widen the segment.
      const status = windowCount >= 10 ? 'ok' : 'warn';
      const reason = status === 'ok'
        ? 'Pipeline is workable.'
        : `Only ${windowCount} sliding window${windowCount === 1 ? '' : 's'} fit. ` +
          'Foundation training works but with limited variety; consider a longer segment for better adapter quality.';
      return { mode, status, reason, totalRows, minWindow, windowCount };
    }
    const suggestedHorizon = Math.max(1, totalRows - seqLen);
    const suggestedSeqLen = Math.max(1, totalRows - horizon);
    return {
      mode,
      status: 'error',
      reason:
        `Segment has ${totalRows} rows but lookback + horizon = ${minWindow} ` +
        `would need at least ${minWindow}. ` +
        `Reduce horizon to ≤ ${suggestedHorizon}, reduce lookback to ≤ ${suggestedSeqLen}, ` +
        `or use a segment with at least ${minWindow} rows.`,
      totalRows, minWindow, windowCount: 0,
      suggested: {
        horizon: suggestedHorizon,
        sequenceLength: suggestedSeqLen,
        totalRows: minWindow,
      },
    };
  }

  // ---- Custom kinds: adaptive per-split window check --------------------
  // Mirrors the trainer's `resolve_split_rows`: splits are resized
  // horizon-aware so train + validation each hold one full window when
  // the data allows. Feasibility only depends on totalRows.
  const ds = dataSplit || {};
  const testSplit = Number(ds.test_split ?? DEFAULT_TEST_SPLIT);
  const valSplit = Number(ds.validation_split ?? DEFAULT_VALIDATION_SPLIT);
  const splits = computeAdaptiveSplitSizes(totalRows, testSplit, valSplit, minWindow);

  const sequences = {
    train: Math.max(0, splits.trainRows - minWindow + 1),
    val:   Math.max(0, splits.valRows   - minWindow + 1),
    test:  Math.max(0, splits.testRows  - minWindow + 1),
  };

  if (totalRows >= 2 * minWindow) {
    // Trainable. Warn when the fit is tight: no test window (test
    // metrics get skipped) or very few sequences overall.
    const lowSplits = Object.entries(sequences)
      .filter(([name, n]) => (name === 'test' ? n === 0 : n < 5))
      .map(([name, n]) => (name === 'test' && n === 0 ? 'test (no held-out window — test metrics will be skipped)' : name));
    if (lowSplits.length === 0 && sequences.train >= 5 && sequences.val >= 5) {
      return {
        mode, status: 'ok',
        reason: 'Pipeline is workable.',
        totalRows, splits, sequences, minWindow,
      };
    }
    return {
      mode, status: 'warn',
      reason: `Workable, but tight: ${formatList(lowSplits.length ? lowSplits : ['train', 'val'])}. ` +
              'Consider more data or a smaller window for better generalization.',
      totalRows, splits, sequences, minWindow,
    };
  }

  // Not trainable: train + validation can't both hold one window.
  // Largest viable values under the adaptive bound (mirror of BE).
  const budget = Math.floor(totalRows / 2);
  const suggestedHorizon = Math.max(1, budget - seqLen);
  const suggestedSeqLen = Math.max(1, budget - horizon);
  const neededTotal = 2 * minWindow;

  return {
    mode, status: 'error',
    reason:
      `Training needs ${minWindow} consecutive rows per window (sequence_length + forecast_horizon), ` +
      `and the train + validation splits need one window each (~${neededTotal} rows total) — the segment has ${totalRows}. ` +
      `Either reduce forecast_horizon to ≤ ${suggestedHorizon}, reduce sequence_length to ≤ ${suggestedSeqLen}, ` +
      `or use a segment with at least ~${neededTotal} rows.`,
    totalRows, splits, sequences, minWindow,
    suggested: {
      horizon: suggestedHorizon,
      sequenceLength: suggestedSeqLen,
      totalRows: neededTotal,
    },
  };
}

/**
 * Suggest sensible default (sequence_length, forecast_horizon) values
 * for a segment so the pipeline is workable out of the box. The user
 * can override either field — these are just the starting values that
 * would otherwise be 1 / blank.
 *
 * Strategy is kind-aware (matches the trainer behavior):
 *
 *   - kind='custom' (existingSequenceLength provided):
 *       Budget = floor(smallest_split / 3) so each split has ~2
 *       training sequences. Pick horizon = budget - seq_len.
 *
 *   - kind='foundation':
 *       Trainer slides windows over the full segment with no
 *       split. Budget = floor(totalRows / 3) so we get ~2 distinct
 *       windows. Allocate ~67% to lookback, ~33% to horizon. Cap
 *       by backbone's max_context / max_horizon when provided.
 *
 *   - In all cases snap to "nice" numbers (12/24/64/...) so users
 *     see clean defaults.
 *
 * Returns null if segment row count can't be estimated.
 *
 * @param {Object}      segment  Segment doc.
 * @param {Object}      dataSplit { test_split, validation_split }. Used
 *                                only in 'custom' mode.
 * @param {Object}      options
 * @param {string?}     options.kind  'custom' (default) or 'foundation'.
 * @param {number?}     options.existingSequenceLength  Required in
 *                                     'custom' mode (the blueprint's
 *                                     fixed sequence_length).
 * @param {number?}     options.maxHorizon              Cap for forecastHorizon.
 * @param {number?}     options.maxContext              Cap for sequenceLength.
 */
export function suggestPipelineDefaults(segment, dataSplit, options = {}) {
  const totalRows = estimateSegmentRows(segment);
  if (totalRows === null || totalRows < 6) return null;

  const kindNorm = String(options.kind || 'custom').toLowerCase();
  const isFoundation = kindNorm === 'foundation';
  const maxHorizon = parseInt(options.maxHorizon, 10);
  const maxContext = parseInt(options.maxContext, 10);

  // ---- Foundation mode: full-segment budget, no splits ---------------
  // Target ~3 sliding windows so the trainer has at least some
  // variety for held-out validation. Allocate budget across
  // (lookback, horizon) with lookback dominant (~2x horizon).
  if (isFoundation) {
    // Reserve enough for at least 2-3 sliding windows: budget for
    // ONE window = floor(totalRows / 3). This guarantees ~3 windows
    // of size (lookback + horizon).
    const budget = Math.max(2, Math.floor(totalRows / 3));
    let horizon = niceNumber(Math.max(1, Math.floor(budget / 3)));
    let lookback = niceNumber(Math.max(1, budget - horizon));
    if (Number.isFinite(maxHorizon) && maxHorizon > 0) horizon = Math.min(horizon, maxHorizon);
    if (Number.isFinite(maxContext) && maxContext > 0) lookback = Math.min(lookback, maxContext);
    // After capping, ensure lookback >= horizon (lookback shorter
    // than horizon makes no temporal sense).
    if (lookback < horizon) lookback = niceNumber(horizon);
    // Final safety: lookback + horizon must fit in totalRows.
    if (lookback + horizon > totalRows) {
      horizon = niceNumber(Math.max(1, Math.floor(totalRows / 4)));
      lookback = niceNumber(Math.max(1, totalRows - horizon));
    }
    return {
      forecastHorizon: Math.max(1, horizon),
      sequenceLength: Math.max(1, lookback),
    };
  }

  // ---- Custom mode: smallest-split budget ------------------------------
  const ds = dataSplit || {};
  const testSplit = clamp(Number(ds.test_split ?? DEFAULT_TEST_SPLIT), 0, 0.5);
  const valSplit = clamp(Number(ds.validation_split ?? DEFAULT_VALIDATION_SPLIT), 0, 0.5);
  const splits = computeSplitSizes(totalRows, testSplit, valSplit);
  const smallest = Math.max(2, Math.min(splits.trainRows, splits.valRows, splits.testRows));
  const budget = Math.max(2, Math.floor(smallest / 3));

  // The budget is the MAX horizon the data can SUPPORT - it must NOT be the
  // default. Defaulting to it produced absurd horizons (e.g. 1024), which for
  // tree models (XGBoost/sklearn) means one model per step -> OOM. Default to
  // a modest horizon, and cap tree frameworks much tighter.
  const isEstimatorFw = /xgboost|sklearn|scikit/i.test(String(options.framework || ''));
  const defaultHorizonCap = isEstimatorFw ? 12 : 24;

  const existing = parseInt(options.existingSequenceLength, 10);
  if (Number.isFinite(existing) && existing > 0) {
    const remainingForHorizon = Math.max(1, budget - existing);
    let horizon = Math.min(niceNumber(remainingForHorizon), defaultHorizonCap);
    if (Number.isFinite(maxHorizon) && maxHorizon > 0) horizon = Math.min(horizon, maxHorizon);
    return { forecastHorizon: Math.max(1, niceNumber(horizon)), sequenceLength: existing };
  }

  // Fallback for custom mode with no fixed seq_len (shouldn't happen
  // in practice, but handled symmetrically): split the budget.
  let horizon = Math.min(niceNumber(Math.max(1, Math.floor(budget / 3))), defaultHorizonCap);
  let lookback = niceNumber(Math.max(1, budget - horizon));
  if (Number.isFinite(maxHorizon) && maxHorizon > 0) horizon = Math.min(horizon, maxHorizon);
  if (Number.isFinite(maxContext) && maxContext > 0) lookback = Math.min(lookback, maxContext);
  if (lookback < horizon) lookback = niceNumber(horizon);
  return {
    forecastHorizon: Math.max(1, horizon),
    sequenceLength: Math.max(1, lookback),
  };
}

// Snap a number down to the nearest "nice" value so the UI shows
// clean defaults like 12 / 24 / 64 instead of 17 / 23 / 71.
const NICE_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 12, 14, 24, 30, 48, 64, 96, 128, 192, 256, 384, 512, 768, 1024];
function niceNumber(n) {
  for (let i = NICE_NUMBERS.length - 1; i >= 0; i--) {
    if (NICE_NUMBERS[i] <= n) return NICE_NUMBERS[i];
  }
  return 1;
}

/**
 * Compute the minimum total segment row count needed for a given
 * (sequence_length, forecast_horizon, data_split, kind) combination.
 * Used by the wizard's Training step where there's no segment yet.
 *
 * Kind-aware:
 *   - 'custom': adaptive splits — train + validation each need one
 *     window, so 2 * minWindow (fractions don't affect feasibility).
 *   - 'foundation': just minWindow (no splits).
 */
export function minRowsForConfig({ sequenceLength, forecastHorizon, dataSplit, kind = 'custom' }) {
  const seqLen = parseInt(sequenceLength, 10);
  const horizon = parseInt(forecastHorizon, 10);
  if (!Number.isFinite(seqLen) || !Number.isFinite(horizon)) return null;
  const minWindow = seqLen + horizon;
  const kindNorm = String(kind || 'custom').toLowerCase();
  if (kindNorm === 'foundation') {
    return minWindow;
  }
  return 2 * minWindow;
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

function formatList(arr) {
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
}
